import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { StatCard } from "./StatCard";
import { Truck } from "../../types";
import { colors } from "../../utils/colors";

interface StatCardsProps {
  trucks: Truck[];
  onFilterChange?: (filter: "all" | "available" | "unavailable") => void;
  currentFilter?: "all" | "available" | "unavailable";
  lastUpdated?: Date;
  isRefetching?: boolean;
}

export const StatCards = ({
  trucks,
  onFilterChange,
  currentFilter = "all",
  lastUpdated,
  isRefetching = false,
}: StatCardsProps) => {
  const latestByRegistration = trucks.reduce((acc, truck) => {
    const current = acc[truck.registration];
    const currentTime = current
      ? new Date(current.intime).getTime()
      : -Infinity;
    const thisTime = new Date(truck.intime).getTime();

    if (!current || thisTime > currentTime) {
      acc[truck.registration] = truck;
    }
    return acc;
  }, {} as Record<string, Truck>);

  const latestTrucks = Object.values(latestByRegistration);

  const availableCars = latestTrucks.filter(
    (truck) => truck.direction?.toLowerCase() === "out"
  ).length;
  const unavailableCars = latestTrucks.filter(
    (truck) => truck.direction?.toLowerCase() === "in"
  ).length;

  const handleCardClick = (filterType: "available" | "unavailable") => {
    if (onFilterChange) {
      if (currentFilter === filterType) {
        onFilterChange("all");
      } else {
        onFilterChange(filterType);
      }
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 1,
        }}
      >
        <StatCard
          type="available"
          title="Cars available"
          value={availableCars}
          isSelected={currentFilter === "available"}
          onClick={() => handleCardClick("available")}
        />
        <StatCard
          type="unavailable"
          title="Cars unavailable"
          value={unavailableCars}
          isSelected={currentFilter === "unavailable"}
          onClick={() => handleCardClick("unavailable")}
        />
      </Box>
      <Stack sx={{ display: "flex", gap: "2px" }}>
        <Typography
          variant="caption"
          sx={{
            color: colors.secondaryText,
            fontSize: "0.75rem",
            fontFamily: "SigmaSans",
          }}
        >
          Tap a card to filter vehicles based on availability.
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: colors.secondaryText,
            fontSize: "0.75rem",
            fontFamily: "SigmaSans",
            fontStyle: "italic",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          Last updated:{" "}
          {lastUpdated
            ? lastUpdated.toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
            : new Date().toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
          {isRefetching && (
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: colors.volvoBlue,
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                  "100%": { opacity: 1 },
                },
              }}
            />
          )}
        </Typography>
      </Stack>
    </Box>
  );
};
